import React from 'react';
import { User, Calendar, MessageSquare } from 'lucide-react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ApprovalRequest } from '../../../services/approvalService';
import { format, formatDistanceToNow } from 'date-fns';

interface ApprovalRequestCardProps {
  request: ApprovalRequest;
  onApprove?: (request: ApprovalRequest) => void;
  onReject?: (request: ApprovalRequest) => void;
  onView?: (request: ApprovalRequest) => void;
  onDelete?: (request: ApprovalRequest) => void;
  isApprover?: boolean;
  isRequester?: boolean;
}

const formatResourceType = (resourceType: string) =>
  resourceType
    .split(/(?=[A-Z])/)
    .join(' ')
    .replace(/^\w/, (c) => c.toUpperCase());

const statusConfig = {
  pending: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50/30',
    badge: 'bg-amber-100 text-amber-800 border-amber-200/60',
    dot: 'bg-amber-500',
  },
  approved: {
    border: 'border-l-emerald-500',
    bg: 'bg-emerald-50/20',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200/60',
    dot: 'bg-emerald-500',
  },
  rejected: {
    border: 'border-l-rose-500',
    bg: 'bg-rose-50/20',
    badge: 'bg-rose-100 text-rose-800 border-rose-200/60',
    dot: 'bg-rose-500',
  },
};

const ApprovalRequestCard: React.FC<ApprovalRequestCardProps> = ({
  request,
  onApprove,
  onReject,
  onView,
  onDelete,
  isApprover = false,
  isRequester = false,
}) => {
  const status = request.status;
  const theme = statusConfig[status] ?? statusConfig.pending;
  const actionLabels: Record<string, string> = {
    create: 'Create',
    update: 'Update',
    delete: 'Delete',
  };
  const actionColors: Record<string, string> = {
    create: 'bg-emerald-100 text-emerald-700 border-emerald-200/60',
    update: 'bg-sky-100 text-sky-700 border-sky-200/60',
    delete: 'bg-rose-100 text-rose-700 border-rose-200/60',
  };

  const keyFieldsByType: Record<string, string[]> = {
    asset: ['name', 'code', 'moduleType', 'serialNumber'],
    manufacturer: ['name', 'contactPerson', 'email', 'phone'],
    customer: ['name', 'contactPerson', 'email', 'phone'],
    location: ['name', 'address', 'city', 'state'],
    user: ['name', 'email', 'phone'],
    checkpoint: ['name', 'description', 'category'],
    projectType: ['name', 'description', 'duration'],
  };
  const fields = keyFieldsByType[request.resourceType] || ['name', 'title', 'description', 'email'];
  const previewItems = fields
    .filter((f) => request.requestData[f] !== undefined && request.requestData[f] !== null && request.requestData[f] !== '')
    .slice(0, 3)
    .map((f) => {
      const label = f.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase()).trim();
      return `${label}: ${String(request.requestData[f]).slice(0, 30)}${String(request.requestData[f]).length > 30 ? '…' : ''}`;
    });

  const sentForReviewDate = request.createdAt
    ? format(new Date(request.createdAt), 'MMM d, yyyy • HH:mm')
    : null;
  const daysAgoText = request.createdAt
    ? formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })
    : null;

  return (
    <div
      className={`
        group relative overflow-hidden rounded-xl border border-gray-200/80 bg-white
        shadow-sm hover:shadow-lg hover:shadow-gray-200/50 hover:border-gray-300/80
        transition-all duration-300 ease-out font-[family-name:var(--font-outfit)]
        border-l-4 ${theme.border} hover:-translate-y-0.5
      `}
    >
      {/* Sent for review strip */}
      {(sentForReviewDate || daysAgoText) && (
        <div className={`px-4 py-2 ${theme.bg} border-b border-gray-100/80`}>
          <p className="text-[11px] font-medium text-gray-600 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              Sent for review
            </span>
            <span className="font-bold text-gray-900 normal-case">{daysAgoText}</span>
            {sentForReviewDate && (
              <span className="text-gray-500 normal-case text-[10px]">({sentForReviewDate})</span>
            )}
          </p>
        </div>
      )}

      <div className="p-4">
        {/* Top row: action + resource + status */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold border ${actionColors[request.action] ?? 'bg-gray-100 text-gray-700 border-gray-200/60'}`}
          >
            {actionLabels[request.action] ?? request.action}
          </span>
          <span className="text-xs font-semibold text-gray-800">
            {formatResourceType(request.resourceType)}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${theme.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
            {status}
          </span>
          <span className="text-[11px] text-gray-500 ml-auto flex items-center gap-1 tabular-nums">
            <Calendar className="w-3 h-3 text-gray-400" />
            {format(new Date(request.createdAt), 'MMM d, HH:mm')}
          </span>
        </div>

        {/* Requester + preview */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0 shrink-0 px-2.5 py-1.5 rounded-lg bg-gray-50/80 border border-gray-100">
            <User className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-[11px] font-medium text-gray-700 truncate">{request.requesterName}</span>
          </div>
          {previewItems.length > 0 && (
            <div className="flex-1 min-w-0 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
              {previewItems.map((item, i) => (
                <span key={i} className="truncate max-w-[160px]" title={item}>
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Approver comments */}
        {request.approverComments && (
          <div className="flex items-start gap-2 mb-3 p-3 rounded-lg bg-gray-50/90 border border-gray-100">
            <MessageSquare className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[11px] text-gray-700 leading-snug line-clamp-2">
                {request.approverComments}
                {request.approverName && (
                  <span className="text-gray-500 ml-1">— {request.approverName}</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          {onView && (
            <Button
              icon="pi pi-eye"
              className="p-button-text p-button-sm !rounded-lg hover:!bg-gray-100"
              tooltip="View details"
              tooltipOptions={{ position: 'top' }}
              onClick={() => onView(request)}
            />
          )}
          {isApprover && request.status === 'pending' && (
            <>
              {onApprove && (
                <Button
                  icon="pi pi-check"
                  className="p-button-text p-button-sm p-button-success !rounded-lg"
                  tooltip="Approve"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => onApprove(request)}
                />
              )}
              {onReject && (
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-danger !rounded-lg"
                  tooltip="Reject"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => onReject(request)}
                />
              )}
            </>
          )}
          {isRequester && request.status === 'pending' && onDelete && (
            <Button
              icon="pi pi-trash"
              className="p-button-text p-button-sm p-button-danger !rounded-lg"
              tooltip="Delete request"
              tooltipOptions={{ position: 'top' }}
              onClick={() => onDelete(request)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalRequestCard;
